/**
 * RelatedTasksCore Component
 *
 * Core logic for selecting related tasks. Drives an independent, server-backed
 * picker (`useRelatedTasksPicker`) with its own server-side filters and lazy
 * infinite-scroll pagination — fully decoupled from the global tasks list and
 * the sidebar's `taskFilters`.
 *
 * Features:
 * - Server-side filters (name, assignee, date range, location)
 * - Lazy infinite-scroll list of pickable tasks
 * - Multi-select with circular dependency detection
 * - Selected tasks section (hydrated by the parent, top) + available list (bottom)
 *
 * @module components/tasks/wizard/steps/Step4RelatedTasks/RelatedTasksListPanel/core
 */

import { useState, useMemo, useCallback } from 'react';
import { Stack, Alert, TextInput, Group, Text } from '@mantine/core';
import {
  IconAlertCircle,
  IconMapPin,
  IconCalendar,
  IconLink,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { useWorkspaceMemberSuggestions } from '@/hooks';
import { MemberSelectView } from '@/components/common/MemberSelect';
import { detectCircularDependency } from '@/utils/taskHelpers';
import { getMemberLabel } from '@/utils/getMemberLabel';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SearchAndFilters } from '@/components/ui/SearchAndFilters';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { ICON_SIZE_SM } from '@/constants/ui';
import type { TaskListItem } from '@/types';
import { useRelatedTasksPicker } from '../../hooks/useRelatedTasksPicker';
import { RelatedTasksFilterPills } from '../../components/RelatedTasksFilterPills';
import { SelectedTaskItem } from '../../components/SelectedTaskItem';
import { AvailableTasksList } from './AvailableTasksList';
import styles from './RelatedTasksCore.module.css';
import sharedStyles from '@/components/common/styles/ScrollContainerLayout.module.css';

const DATE_FORMAT_PL = 'DD.MM.YYYY';
const DATE_FORMAT_EN = 'MM/DD/YYYY';

/**
 * Mantine v9 date pickers emit a 'YYYY-MM-DD' string; parse to a Date (or null)
 * so the picker state stays Date-typed for buildTaskFiltersParams (mirrors
 * TaskFilters). Invalid input collapses to null.
 */
function parsePickerDate(value: string | Date | null): Date | null {
  if (value === null) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

interface RelatedTasksCoreProps {
  selectedTaskIds: string[];
  selectedTasks: TaskListItem[];
  onTasksSelect: (taskIds: string[]) => void;
  onCacheTasks: (items: TaskListItem[]) => void;
  editingTaskId?: string | null;
}

export function RelatedTasksCore({
  selectedTaskIds,
  selectedTasks,
  onTasksSelect,
  onCacheTasks,
  editingTaskId,
}: RelatedTasksCoreProps) {
  const { t, i18n } = useTranslation();

  // Circular dependency error state
  const [circularDependencyError, setCircularDependencyError] = useState<
    string | null
  >(null);

  // Filters expanded state
  const [filtersExpanded, { toggle: toggleFilters }] = useDisclosure(false);

  // Independent, server-backed picker — fetch is gated on mount (the panel only
  // renders this component while open), so the list is lazy-loaded on open.
  const {
    tasks,
    nameSearch,
    setNameSearch,
    assigneeId,
    setAssigneeId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    locationSearch,
    setLocationSearch,
    isLoading,
    hasLoaded,
    hasNextPage,
    error,
    loadMore,
    hasActiveFilters,
    clearFilters,
  } = useRelatedTasksPicker({
    enabled: true,
    editingTaskId,
    selectedTaskIds,
  });

  // Assignee options from workspace members (id + name) — server-side filter.
  const { members, isLoading: isMembersLoading } =
    useWorkspaceMemberSuggestions({ enabled: true });
  const assigneeLabel = useMemo(() => {
    if (!assigneeId) return '';
    const match = members.find((member) => member.userId === assigneeId);
    return match ? getMemberLabel(match) : '';
  }, [assigneeId, members]);

  // Locale-specific date format for placeholders
  const dateFormat = useMemo(
    () => (i18n.language === 'pl' ? DATE_FORMAT_PL : DATE_FORMAT_EN),
    [i18n.language]
  );

  const handleDateFromChange = useCallback(
    (value: string | Date | null) => {
      const next = parsePickerDate(value);
      setDateFrom(next);
      // Keep the range valid: a 'from' after the current 'to' pushes 'to' along.
      if (next && dateTo && next > dateTo) {
        setDateTo(next);
      }
    },
    [setDateFrom, setDateTo, dateTo]
  );

  const handleDateToChange = useCallback(
    (value: string | Date | null) => {
      setDateTo(parsePickerDate(value));
    },
    [setDateTo]
  );

  // Circular-dependency check sees both the loaded picker page and the already
  // selected tasks (union) — no broader than before, just decoupled sources.
  const cycleGraph = useMemo<TaskListItem[]>(() => {
    const byId = new Map<string, TaskListItem>();
    selectedTasks.forEach((task) => byId.set(task.id, task));
    tasks.forEach((task) => byId.set(task.id, task));
    return Array.from(byId.values());
  }, [selectedTasks, tasks]);

  // Handle task selection (toggle in/out of selection)
  const handleTaskSelect = useCallback(
    (taskId: string) => {
      const newSelectedIds = selectedTaskIds.includes(taskId)
        ? selectedTaskIds.filter((id) => id !== taskId)
        : [...selectedTaskIds, taskId];

      // Validate circular dependency (ONLY during edit mode)
      if (editingTaskId) {
        const hasCircular = detectCircularDependency(
          editingTaskId,
          newSelectedIds,
          cycleGraph
        );
        if (hasCircular) {
          setCircularDependencyError(t('validation.circularDependency'));
          return;
        }
      }

      // Cache the just-selected task so the parent can render it without refetch.
      const picked = tasks.find((task) => task.id === taskId);
      if (picked) onCacheTasks([picked]);

      setCircularDependencyError(null);
      onTasksSelect(newSelectedIds);
    },
    [
      selectedTaskIds,
      editingTaskId,
      cycleGraph,
      tasks,
      onCacheTasks,
      onTasksSelect,
      t,
    ]
  );

  // Handle task removal
  const handleRemove = useCallback(
    (taskId: string) => {
      onTasksSelect(selectedTaskIds.filter((id) => id !== taskId));
      setCircularDependencyError(null);
    },
    [selectedTaskIds, onTasksSelect]
  );

  return (
    <Stack gap="md" className={styles.container}>
      {/* Circular dependency error alert */}
      {circularDependencyError && (
        <Alert
          variant="light"
          color="red"
          icon={<IconAlertCircle size={ICON_SIZE_SM} />}
        >
          {circularDependencyError}
        </Alert>
      )}

      {/* Task search filters (SearchAndFilters pattern) */}
      <SearchAndFilters variant="compact">
        <SearchAndFilters.Header>
          <SearchAndFilters.SearchBar
            placeholder={t('wizard.step4.filters.nameSearch')}
            value={nameSearch}
            onChange={setNameSearch}
          />
          <SearchAndFilters.FilterToggle
            expanded={filtersExpanded}
            hasActiveFilters={hasActiveFilters}
            onClick={toggleFilters}
          />
        </SearchAndFilters.Header>

        <SearchAndFilters.Section
          hasContent={hasActiveFilters || filtersExpanded}
        >
          {/* Pills when collapsed */}
          <SearchAndFilters.Pills show={!filtersExpanded && hasActiveFilters}>
            <RelatedTasksFilterPills
              assignee={assigneeLabel || null}
              location={locationSearch}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onRemoveAssignee={() => setAssigneeId(null)}
              onRemoveLocation={() => setLocationSearch('')}
              onRemoveDateRange={() => {
                setDateFrom(null);
                setDateTo(null);
              }}
            />
          </SearchAndFilters.Pills>

          {/* Filter controls when expanded */}
          <SearchAndFilters.FiltersPanel expanded={filtersExpanded}>
            <Stack gap="sm">
              {/* Assignee — shared member Select (reuses already-fetched members) */}
              <MemberSelectView
                label={t('filters.assignee')}
                placeholder={t('filters.assigneePlaceholder')}
                value={assigneeId}
                onChange={setAssigneeId}
                members={members}
                isMembersLoading={isMembersLoading}
              />

              {/* Location — free-text server filter */}
              <TextInput
                label={t('filters.location')}
                placeholder={t('filters.locationPlaceholder')}
                value={locationSearch}
                onChange={(event) =>
                  setLocationSearch(event.currentTarget.value)
                }
                leftSection={<IconMapPin size={ICON_SIZE_SM} />}
              />

              {/* Date Range */}
              <Group grow>
                <DatePickerInput
                  label={t('filters.dateFrom')}
                  placeholder={dateFormat}
                  valueFormat={dateFormat}
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  leftSection={<IconCalendar size={ICON_SIZE_SM} />}
                  clearable
                  popoverProps={{
                    zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
                    withinPortal: true,
                  }}
                />

                <DatePickerInput
                  label={t('filters.dateTo')}
                  placeholder={dateFormat}
                  valueFormat={dateFormat}
                  value={dateTo}
                  onChange={handleDateToChange}
                  leftSection={<IconCalendar size={ICON_SIZE_SM} />}
                  clearable
                  minDate={dateFrom || undefined}
                  popoverProps={{
                    zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
                    withinPortal: true,
                  }}
                />
              </Group>

              {/* Clear All Button */}
              {hasActiveFilters && (
                <ClearFiltersButton onClick={clearFilters} fullWidth />
              )}
            </Stack>
          </SearchAndFilters.FiltersPanel>
        </SearchAndFilters.Section>
      </SearchAndFilters>

      {/* Selected tasks — header + scrollable list */}
      {selectedTasks.length > 0 && (
        <div className={styles.selectedSection}>
          <div className={sharedStyles.scrollContainer}>
            <div className={sharedStyles.header}>
              <Group gap="xs">
                <IconLink size={ICON_SIZE_SM} />
                <Text size="sm" fw={500}>
                  {t('wizard.step4.selectedTasks_plural', {
                    count: selectedTasks.length,
                  })}
                </Text>
              </Group>
            </div>

            <AppScrollArea fill>
              <Stack gap="xs" className={styles.selectedScrollList}>
                {selectedTasks.map((task) => (
                  <SelectedTaskItem
                    key={task.id}
                    task={task}
                    onRemove={handleRemove}
                  />
                ))}
              </Stack>
            </AppScrollArea>
          </div>
        </div>
      )}

      {/* Available tasks — server-backed, lazy infinite-scroll list */}
      <div className={styles.availableSection}>
        <AvailableTasksList
          tasks={tasks}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          hasNextPage={hasNextPage}
          error={error}
          hasActiveFilters={hasActiveFilters}
          onTaskSelect={handleTaskSelect}
          onLoadMore={loadMore}
        />
      </div>
    </Stack>
  );
}
